/**
 * P17 — §7.8's location picker: **one field, three ways to fill it** (D51).
 *
 * P7 shipped the third input alone — a text box carrying `autocomplete="street-address"`, so the
 * OS offers a saved address at zero network cost and zero API cost, storing `source: manual`. This
 * component keeps that path byte-for-byte and adds the two D92 deferred:
 *
 *  - **The device fix** (`source: device`) — one Geolocation read, **prompted on arrival at this
 *    step rather than behind a control**, with the purpose string on screen beside it, committed as
 *    the pin with no address proposal (D169/D170). The **device's raw coordinates are the pin**.
 *    There is no *"use current location"* button: the step exists to capture a position, so making
 *    the technician ask for the thing the step is for was a tap that only ever had one answer, and
 *    the OS prompt is itself the consent surface. The one-read, never-re-prompted rule is unchanged
 *    — a `useRef` latch makes the capture fire once per mount, which is also what keeps StrictMode's
 *    double-invoked effect from asking twice.
 *  - **Search** (`source: places`) — Places Autocomplete under one session token, restricted to
 *    Israel, biased to the device fix only when one is already in hand.
 *
 * **They compose, they do not compete** (D51). Search fills the text; "use current location" sets
 * the pin; each is a valid save alone. That sentence decides the one case where they overlap:
 * selecting a place while a **device** pin is held adopts the place's text and **keeps the pin
 * captured at the gate**, because PEC-29 makes the device fix authoritative over a postal centroid
 * that can sit tens of metres away on the wrong side of a block. Nothing overwrites that pin
 * silently — the swap is offered as an explicit action and taken only on a tap, which is D66's
 * *confirm or move* in the one shape this scope can reach (§6, and the deviation logged for it).
 *
 * **Degradation is the default, not the fault path.** With no key provisioned, quota exhausted, the
 * device in a parking basement, or the permission denied, this is the field P7 shipped and the
 * install completes with `source: manual` and no coordinates
 * ([§13 "The address is required and the degraded path works (D51)"](../../../../docs/prds/selogate-prd.md)). **A denied permission retires the control for
 * the rest of the flow** and is never re-prompted — §7.8 is explicit, and a second prompt is also
 * a second consent request the user already answered.
 *
 * **Nothing here persists.** The raw fix, the suggestions, and the session token live in component
 * state for the life of the picker and go with it (`device-cache.md`); the only thing that leaves
 * is the payload the caller submits.
 *
 * **Both new inputs are live in every stage since D170** — see `complianceGate.ts`, which carries
 * the whole decision and the two questions still riding on it. They were dark from the day this
 * component shipped until 2026-08-18, which is why **every branch below keeps working with the
 * gate closed**: that is not legacy code. Part D's sign-off table is still empty, a contrary
 * answer from counsel re-closes the gate, and with it closed this component renders exactly what
 * P7 shipped.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Icon, LinkButton } from '../../../components'
import {
  geolocationSupported,
  readDevicePosition,
  type GeolocationFailureReason,
} from '../../../device-capabilities/geolocation'
import { roundCoordinate, mapsConfigured, type MapsFailureReason } from '../../../maps/config'
import { beginSession, resolvePlace, suggestPlaces, type PlaceSuggestion } from '../../../maps/places'
import { useTranslation } from '../../../i18n/I18nProvider'
import { COORDINATE_INPUTS_ENABLED } from './complianceGate'
import type { LocationInput } from '../api'

/** 🔸 Keystrokes settle for this long before a billed autocomplete call goes out. */
const SUGGEST_DEBOUNCE_MS = 300

/** 🔸 Below this, an autocomplete query is noise that still costs a session. */
const MIN_SUGGEST_LENGTH = 3

/**
 * What the field is telling the user right now. One slot, because these are mutually exclusive
 * moments rather than a log — and because a stack of notices is how a degraded path stops reading
 * as a working one.
 *
 * **A held pin is deliberately not one of these.** It is rendered from `devicePin` itself, below,
 * because "the gate is pinned" is a *state* the technician can look up at any time — a notice is a
 * moment, and this one would be cleared by the next thing the field had to say while the pin it
 * announced was still held.
 */
type Notice =
  | { readonly kind: 'geo'; readonly reason: GeolocationFailureReason }
  | { readonly kind: 'maps'; readonly reason: MapsFailureReason }
  | { readonly kind: 'pin-kept' }

export interface LocationPickerProps {
  readonly value: LocationInput
  readonly onChange: (next: LocationInput) => void
  /** The `id` the caller's own `<label htmlFor>` points at. */
  readonly inputId: string
  /** Rendered under the field when the caller's validation refused the value. */
  readonly error?: string
  readonly disabled?: boolean
  /**
   * D33's dashboard variant omits "use current location" — *"a manager at a desk is not at the
   * gate"*. Building that dashboard is Stage 2's; **supporting the suppression is this PRD's**, so
   * the prop exists with no Stage-1 caller passing `false`.
   */
  readonly allowDevicePosition?: boolean
}

export function LocationPicker({
  value,
  onChange,
  inputId,
  error,
  disabled = false,
  allowDevicePosition = true,
}: LocationPickerProps): JSX.Element {
  const { t, locale } = useTranslation('location')

  /**
   * The device fix, held separately from `value` for one reason: `value.lat`/`value.lng` may
   * currently be a *place's* coordinates, and the offer to go back to the pin captured at the gate
   * has to survive that. Dropping it into the payload and reading it back would lose exactly the
   * fact PEC-29 says is authoritative.
   */
  const [devicePin, setDevicePin] = useState<{ readonly lat: number; readonly lng: number } | undefined>(
    undefined,
  )
  const [capturing, setCapturing] = useState(false)
  /** §7.8: never re-prompted in-flow. Set once, never cleared — that is the whole rule. */
  const [permissionDenied, setPermissionDenied] = useState(false)
  const [notice, setNotice] = useState<Notice | undefined>(undefined)

  const [suggestions, setSuggestions] = useState<readonly PlaceSuggestion[]>([])
  const [resolvingPlaceId, setResolvingPlaceId] = useState<string | undefined>(undefined)
  /**
   * One token per keystroke-run **plus** the Details call that resolves it (D51's session-token
   * billing). Minted lazily on the first query and dropped on every resolution, so a run that ends
   * in a selection bills once and the next run starts a new session.
   */
  const sessionRef = useRef<ReturnType<typeof beginSession> | undefined>(undefined)
  const suggestAbortRef = useRef<AbortController | undefined>(undefined)
  /**
   * Suppresses the next autocomplete round when the text changed because *we* set it — a reverse
   * geocode's proposal or a selected suggestion. Without it, adopting a suggestion immediately
   * queries Autocomplete with the address it just resolved: a billed call whose best possible
   * answer is the row the user already tapped.
   */
  const skipNextSuggestRef = useRef(false)
  /**
   * **Whether the text in the field is the user's own.** The autocomplete round below runs off
   * `value.formatted` rather than off a keystroke, which is what lets OS autofill be queried on
   * equal terms — and it is also why a *handed* address queried too: the update flow (D267/D176)
   * mounts this picker on a gate's stored address, so arriving at the location editor opened the
   * suggestion drawer over the address already saved, with a billed call behind it, before the
   * manager had said anything about wanting to change it.
   *
   * **This is a latch and not a one-shot, deliberately.** `skipNextSuggestRef` beside it suppresses
   * exactly one round because it answers a text *this component* just set; here the text arrives
   * from the caller, which on the update door happens **after** mount — `updateLocationAction` is
   * disabled until the gate read lands today, but a mount-time snapshot would rest on that
   * ordering, and a picker that is handed its value one frame later is the same screen. So the
   * drawer stays shut until `handleText` fires, which is the one path a keystroke *or* an autofill
   * event reaches, and from that first edit onwards suggestions follow the field exactly as before.
   */
  const editedRef = useRef(false)
  /**
   * The capture fires from an effect now rather than from a tap, so something has to make it fire
   * **once**. `main.tsx` renders under `StrictMode`, which double-invokes every effect, and this
   * one raises an OS permission dialog — the second invocation would be a second consent request
   * for a question already asked, which is exactly what §7.8 forbids on the denial path.
   */
  const capturedRef = useRef(false)

  /**
   * **The D73 gate comes first, and it governs both inputs** (`complianceGate.ts`). With it closed
   * this component is exactly the field P7 shipped, which is why it is read here rather than at
   * either call site: a caller cannot forget it, and there is no second path to a coordinate.
   */
  const coordinateInputs = COORDINATE_INPUTS_ENABLED
  const networkInputs = coordinateInputs && mapsConfigured()
  const deviceInputAvailable =
    coordinateInputs && allowDevicePosition && !permissionDenied && geolocationSupported() && !disabled

  const emit = useCallback(
    (next: LocationInput) => {
      onChange(next)
    },
    [onChange],
  )

  /** Text-only edits. **The pin is never dropped and never re-geocoded by typing** (D66). */
  function handleText(text: string): void {
    // The one place the field's text becomes the user's own — `onChange`, so an OS autofill that
    // fires no keystroke still counts. Everything else that reaches `emit()` is this component
    // answering itself and must not open the drawer.
    editedRef.current = true
    if (devicePin !== undefined) {
      emit({ formatted: text, source: 'device', lat: devicePin.lat, lng: devicePin.lng })
      return
    }
    if (
      value.source === 'places' &&
      value.placeId !== undefined &&
      value.lat !== undefined &&
      value.lng !== undefined
    ) {
      // A text correction on a resolved place keeps the place. D66 refuses the automatic downgrade
      // to `manual` outright — it *"would manufacture backlog out of a text correction."*
      emit({ formatted: text, source: 'places', placeId: value.placeId, lat: value.lat, lng: value.lng })
      return
    }
    emit({ formatted: text, source: 'manual' })
  }

  async function captureDevicePosition(): Promise<void> {
    setCapturing(true)
    setNotice(undefined)
    try {
      const fix = await readDevicePosition()
      if (!fix.ok) {
        if (fix.reason === 'denied') setPermissionDenied(true)
        setNotice({ kind: 'geo', reason: fix.reason })
        return
      }

      const lat = roundCoordinate(fix.position.lat)
      const lng = roundCoordinate(fix.position.lng)
      setDevicePin({ lat, lng })
      setSuggestions([])

      // **D169/D170: there is no reverse geocode, and this is the whole of the capture.** The
      // pin was always committed before the geocode was attempted, *"so a denied or quota-limited
      // geocode costs the label and never the capture"* — which is why removing the call costs the
      // address auto-fill and nothing else. The technician types the address; D169(d) records that
      // the auto-fill *"was always a convenience and never a source of truth."*
      //
      // **Do not re-add a proposal here.** Geocoding cannot run under the only key restriction this
      // design permits (D169(a)), the frontend/backend key split is refused on three grounds
      // (D169(b)), and `searchNearby` is refused on correctness — it names the nearest *place*,
      // which at a residential gate is often a business across the street: an authoritative-looking
      // address that is quietly wrong, at the one moment a human is standing there and knows better.
      emit({ formatted: value.formatted, source: 'device', lat, lng })
    } finally {
      setCapturing(false)
    }
  }

  /**
   * **The prompt is the arrival, not a tap.** §7.8's just-in-time rule is satisfied by *this step*
   * being where the read happens, with the purpose string on screen while the OS dialog stands;
   * a button in front of it added a tap whose only useful answer was yes. Everything the button
   * guarded is unchanged: one read, `permissionDenied` retires it for the rest of the flow, and a
   * refusal degrades to P7's free-text field rather than blocking the install.
   */
  useEffect(() => {
    if (!deviceInputAvailable || capturedRef.current) return
    capturedRef.current = true
    void captureDevicePosition()
    // `captureDevicePosition` is re-created every render and the latch above makes this fire once,
    // so depending on its identity would only re-arm an effect that immediately returns.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceInputAvailable])

  // Autocomplete, debounced and abortable. Runs off `value.formatted` so OS autofill — which fires
  // no keystroke — is queried on equal terms with typing, and gated on `editedRef` so a value the
  // caller handed us is not (see the ref's own note).
  const query = value.formatted.trim()
  useEffect(() => {
    if (skipNextSuggestRef.current) {
      skipNextSuggestRef.current = false
      setSuggestions([])
      return
    }
    if (!editedRef.current || !networkInputs || disabled || query.length < MIN_SUGGEST_LENGTH) {
      setSuggestions([])
      return
    }

    const timer = window.setTimeout(() => {
      suggestAbortRef.current?.abort()
      const controller = new AbortController()
      suggestAbortRef.current = controller
      sessionRef.current ??= beginSession()

      void suggestPlaces({
        input: query,
        sessionToken: sessionRef.current,
        languageCode: locale,
        ...(devicePin === undefined ? {} : { bias: devicePin }),
        signal: controller.signal,
      }).then((result) => {
        if (controller.signal.aborted) return
        if (result.ok) {
          setSuggestions(result.suggestions)
          return
        }
        setSuggestions([])
        // `no-results` and `network` are silent: an address the geocoder does not know is a
        // perfectly good free-text save, and a notice on every unmatched keystroke would read as a
        // broken field. A rejected key or an exhausted quota is worth saying once.
        if (result.reason === 'denied' || result.reason === 'quota') {
          setNotice({ kind: 'maps', reason: result.reason })
        }
      })
    }, SUGGEST_DEBOUNCE_MS)

    return () => window.clearTimeout(timer)
  }, [query, locale, networkInputs, disabled, devicePin])

  useEffect(() => () => suggestAbortRef.current?.abort(), [])

  async function selectSuggestion(suggestion: PlaceSuggestion): Promise<void> {
    setResolvingPlaceId(suggestion.placeId)
    try {
      const token = (sessionRef.current ??= beginSession())
      const resolved = await resolvePlace({
        placeId: suggestion.placeId,
        sessionToken: token,
        languageCode: locale,
      })
      // The token is spent whether or not the resolution succeeded — Google has seen the Details
      // call, so reusing it would bill a second session while looking like thrift.
      sessionRef.current = undefined
      setSuggestions([])
      skipNextSuggestRef.current = true

      if (!resolved.ok) {
        // No coordinates means no `places` row: P3 refuses one, and relabelling it `manual` would
        // claim the text came from somewhere it did not. Keep the text, say what happened.
        emit(
          devicePin === undefined
            ? { formatted: suggestion.text, source: 'manual' }
            : { formatted: suggestion.text, source: 'device', lat: devicePin.lat, lng: devicePin.lng },
        )
        if (resolved.reason !== 'unconfigured') setNotice({ kind: 'maps', reason: resolved.reason })
        return
      }

      if (devicePin !== undefined) {
        // **The pin captured at the gate wins** (PEC-29). The place supplies the label; the swap to
        // its centroid is available below, on a tap, and never happens on its own.
        emit({
          formatted: resolved.place.formatted,
          source: 'device',
          lat: devicePin.lat,
          lng: devicePin.lng,
        })
        setNotice({ kind: 'pin-kept' })
        return
      }

      emit({
        formatted: resolved.place.formatted,
        source: 'places',
        placeId: resolved.place.placeId,
        lat: resolved.place.lat,
        lng: resolved.place.lng,
      })
      setNotice(undefined)
    } finally {
      setResolvingPlaceId(undefined)
    }
  }

  /**
   * D66's *move*, in the only direction this scope has a second candidate for: adopt the address's
   * own coordinates instead of the device fix. Explicit, reversible by re-capturing, and the pin it
   * replaces was never discarded silently.
   */
  async function adoptPlacePin(): Promise<void> {
    const token = beginSession()
    const query2 = value.formatted.trim()
    if (query2.length === 0) return
    const found = await suggestPlaces({ input: query2, sessionToken: token, languageCode: locale })
    if (!found.ok || found.suggestions[0] === undefined) {
      setNotice({ kind: 'maps', reason: found.ok ? 'no-results' : found.reason })
      return
    }
    const resolved = await resolvePlace({
      placeId: found.suggestions[0].placeId,
      sessionToken: token,
      languageCode: locale,
    })
    if (!resolved.ok) {
      setNotice({ kind: 'maps', reason: resolved.reason })
      return
    }
    setDevicePin(undefined)
    skipNextSuggestRef.current = true
    emit({
      formatted: resolved.place.formatted,
      source: 'places',
      placeId: resolved.place.placeId,
      lat: resolved.place.lat,
      lng: resolved.place.lng,
    })
    setNotice(undefined)
  }

  const noticeText = useMemo(() => {
    if (notice === undefined) return undefined
    if (notice.kind === 'pin-kept') return t('pinKept')
    if (notice.kind === 'geo') {
      return notice.reason === 'denied'
        ? t('geoDenied')
        : notice.reason === 'timeout'
          ? t('geoTimeout')
          : t('geoUnavailable')
    }
    return notice.reason === 'quota' ? t('mapsQuota') : t('mapsUnavailable')
  }, [notice, t])

  /**
   * The copy places the pin glyph mid-sentence, so the catalog carries `{{pin}}` and the split
   * happens here: `t()` interpolates strings, and an icon is an element. Passing no vars leaves
   * the marker in place, which is what makes it a seam a translator can move.
   */
  const pinnedParts = useMemo(() => t('pinned').split('{{pin}}'), [t])

  return (
    <div className="location-picker">
      <input
        id={inputId}
        className="input"
        // P7's OS-autofill path, unchanged. Accepting a saved address alone stores `source: manual`.
        autoComplete="street-address"
        value={value.formatted}
        disabled={disabled}
        onChange={(e) => handleText(e.target.value)}
      />

      {/*
        **Directly under the input it completes, and above everything else.** It is a plain list
        rather than a floating popup (see `global.css`), so its position in the flow *is* its
        attachment to the field — anything rendered between the two reads as a separate block and
        the list stops looking like the input's own suggestions. That is what it looked like while
        the capture control sat in the gap.
      */}
      {suggestions.length > 0 ? (
        <ul className="location-suggestions" aria-label={t('suggestionsLabel')}>
          {suggestions.map((suggestion) => (
            <li key={suggestion.placeId}>
              <button
                type="button"
                className="location-suggestion"
                disabled={resolvingPlaceId !== undefined}
                onClick={() => void selectSuggestion(suggestion)}
              >
                {suggestion.text}
              </button>
            </li>
          ))}
          {/*
            **D259 — Google's credit, and it is required rather than polite.** These predictions
            are Places data displayed with no Google Map anywhere in the app, which is the exact
            case Google's policy names; it rides *inside* the panel, as the list's own last row,
            so it appears and disappears with the content it credits and cannot drift away from
            it. A plain `<li>` is legal here because this `<ul>` carries no `listbox` role — the
            options are buttons in a labelled list, not ARIA options — so a non-interactive row
            adds no member to a collection anything counts.

            **The name is a literal and must never become a catalog key.** Google's policy forbids
            modifying the text *Google Maps* in any way, so translating it is the defect, not the
            fix; a key in `location.json` is an invitation to translate it in five places, and
            `catalogs.test.ts` would then hold the five apart rather than together. The screen
            reader gets it from `role="img"` + `aria-label`, because the mark is painted as a
            background (`global.css` says why the scheme fork cannot be a `fill`) and a background
            has no `alt`.
          */}
          <li className="location-attribution">
            <span className="location-attribution-mark" role="img" aria-label="Google Maps" />
          </li>
        </ul>
      ) : null}

      {devicePin === undefined ? (
        deviceInputAvailable ? (
          /*
            The purpose string stands on the screen raising the prompt, not in a settings screen and
            not at launch — §7.8's just-in-time rule is about *where the sentence is* as much as
            about when the OS dialog appears, and with the button gone this is the whole of it.
          */
          <p className="hint">{capturing ? t('deviceCapturing') : t('devicePurpose')}</p>
        ) : null
      ) : (
        <p className="location-pinned">
          {pinnedParts[0]}
          <Icon name="pin" />
          {pinnedParts[1] ?? ''}
        </p>
      )}

      {noticeText === undefined ? null : <p className="hint">{noticeText}</p>}

      {notice?.kind === 'pin-kept' ? (
        <LinkButton onClick={() => void adoptPlacePin()}>{t('useAddressPin')}</LinkButton>
      ) : null}

      {error === undefined ? null : <p className="hint error">{error}</p>}
    </div>
  )
}
